import React from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Archive, ChevronRight, KeyRound, History, Plug, ShieldCheck } from 'lucide-react';

import SettingsCard from '../SettingsCard';

const EVENT_LABELS = {
  login_success: 'Inloggning',
  login_failure: 'Misslyckad inloggning',
  logout: 'Utloggning',
  token_refresh: 'Session förnyad',
};

const EVENT_COLORS = {
  login_success: '#44814E',
  login_failure: '#dc3545',
  logout: '#737B8B',
  token_refresh: '#737B8B',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function summariseUserAgent(ua) {
  if (!ua) return '—';
  let browser = 'Okänd webbläsare';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Chromium|OPR/.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';

  let os = 'Okänt OS';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
  else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Linux/.test(ua)) os = 'Linux';

  return `${browser} · ${os}`;
}

// Account-level pages that live outside the company shell. Listed here rather
// than as bare links above the rail so they read as part of Säkerhet.
const ACCOUNT_LINKS = [
  { to: '/privacy', Icon: ShieldCheck, title: 'Dina personuppgifter', desc: 'Begär radering, stäng ditt personliga konto eller följ ett ärende.' },
  { to: '/connected-apps', Icon: Plug, title: 'Anslutna appar', desc: 'AI-appar som du har gett åtkomst till LedgerLoop.' },
  { to: '/company-closures', Icon: Archive, title: 'Företagsavslut', desc: 'Följ rensningen av företag du har avslutat som ägare.' },
];

// Security section of the user settings — password/sign-in method management and
// login history. Sign-in methods and login events are fetched in the
// UserSettings shell (login history lazy-loads on first visit) and passed in.
const SecuritySection = ({
  signInMethods,
  email,
  passwordBusy,
  handleSendPasswordReset,
  openSetPasswordModal,
  loginEvents,
  loginEventsLoading,
  handleLogoutAll,
}) => {
  const hasPassword = signInMethods?.hasPassword;
  const onlyGoogle = signInMethods && !signInMethods.hasPassword && signInMethods.hasGoogle;

  return (
    <>
      <SettingsCard id="sessioner" icon={KeyRound} title="Inloggade enheter">
        <p>Avsluta dina LedgerLoop-inloggningar i alla webbläsare och på andra enheter, även den här. Du behöver sedan logga in igen.</p>
        <Button variant="outline-primary" onClick={handleLogoutAll}>Logga ut från alla webbläsare</Button>
      </SettingsCard>
      <SettingsCard
        id="losenord"
        icon={KeyRound}
        title={onlyGoogle ? 'Logga in med e-post och lösenord' : 'Lösenord'}
      >

        {signInMethods === null && (
          <p style={{ color: '#737B8B', margin: 0, fontSize: '0.875rem' }}>
            Läser in inloggningsalternativ...
          </p>
        )}

        {hasPassword && (
          <>
            <p style={{ color: '#737B8B', margin: '0 0 1rem', fontSize: '0.875rem' }}>
              Skicka en återställningslänk till <strong>{email}</strong>.
            </p>
            <Button variant="primary" onClick={handleSendPasswordReset} disabled={passwordBusy}>
              {passwordBusy ? 'Skickar...' : 'Skicka återställningslänk'}
            </Button>
          </>
        )}

        {onlyGoogle && (
          <>
            <p style={{ color: '#737B8B', margin: '0 0 1rem', fontSize: '0.875rem' }}>
              Du loggar för tillfället in med Google. Vill du även kunna logga in
              med <strong>{email}</strong> och ett lösenord? Google-inloggningen
              fortsätter att fungera som vanligt.
            </p>
            <Button variant="primary" onClick={openSetPasswordModal} disabled={passwordBusy}>
              Aktivera lösenordsinloggning
            </Button>
          </>
        )}
      </SettingsCard>

      <SettingsCard
        id="inloggningshistorik"
        icon={History}
        title="Inloggningshistorik"
        subtitle="Ser du aktivitet du inte känner igen? Logga ut från alla webbläsare. Återställ ditt LedgerLoop-lösenord om du använder lösenord, eller kontrollera säkerheten i ditt Google-konto om du loggar in med Google."
      >

        {loginEventsLoading && (
          <p style={{ margin: 0, color: '#737B8B', fontSize: '0.875rem' }}>Läser in...</p>
        )}

        {!loginEventsLoading && loginEvents && loginEvents.length === 0 && (
          <p style={{ margin: 0, color: '#737B8B', fontSize: '0.875rem' }}>
            Inga händelser ännu. Information sparas från och med nu.
          </p>
        )}

        {!loginEventsLoading && loginEvents && loginEvents.length > 0 && (
          <div className="user-settings__events">
            {loginEvents.map((evt) => (
              <div key={evt.event_id} className="user-settings__event-row">
                <div className="user-settings__event-main">
                  <span
                    className="user-settings__event-type"
                    style={{ color: EVENT_COLORS[evt.event_type] || '#737B8B' }}
                  >
                    {EVENT_LABELS[evt.event_type] || evt.event_type}
                  </span>
                  <span className="user-settings__event-meta">
                    {summariseUserAgent(evt.user_agent)}
                    {evt.provider === 'google.com' ? ' · Google' : evt.provider === 'password' ? ' · E-post och lösenord' : ''}
                    {evt.ip_address ? ` · ${evt.ip_address}` : ''}
                    {evt.city || evt.country ? ` · ${[evt.city, evt.country].filter(Boolean).join(', ')}` : ''}
                  </span>
                </div>
                <span className="user-settings__event-time">
                  {formatDateTime(evt.occurred_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>

      <SettingsCard id="integritet" icon={ShieldCheck} title="Integritet och anslutningar">
        <ul className="ll-linklist">
          {ACCOUNT_LINKS.map(({ to, Icon, title, desc }) => (
            <li key={to}>
              <Link to={to} className="ll-linklist__row">
                <span className="ll-linklist__icon"><Icon size={16} strokeWidth={1.75} aria-hidden="true" /></span>
                <span className="ll-linklist__text">
                  <span className="ll-linklist__title">{title}</span>
                  <span className="ll-linklist__desc">{desc}</span>
                </span>
                <ChevronRight size={16} className="ll-linklist__chevron" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </SettingsCard>
    </>
  );
};

export default SecuritySection;
